import React, { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import IconButton from '@material-ui/core/IconButton';
import { getCourses } from '../../../../services/program.service';
import { ICONS } from '../../../../../../../constants';
import { CONTENT_DELIVERY_METHOD } from '../../../../../../services/constants';

const useAddCourseHook = (formData, handleCourseChange) => {
  const [showSearchSection, setShowSearchSection] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [shouldCallApi, setShouldCallApi] = useState(true);
  const [selectedCourses, setSelectedCourses] = useState(formData?.courses);
  const [draggableItems, setDraggableItems] = useState([]);
  const [fetchedCourses, setFetchedCourses] = useState([]);
  const [contentDeliveryMethodError, setContentDeliveryMethodError] = useState(false);
  const removeCourseIcon = `${ICONS}remove-course.svg`;
  const fetchCourses = async (search) => {
    if (!search?.label?.trim()) {
      setOptions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getCourses(search?.label, formData?.contentDeliveryMethod, formData?.contentProvider);
      if (response?.status !== "SUCCESS") {
        throw new Error("Failed to fetch courses");
      }

      const formattedOptions = response?.payload?.courses?.map(course => ({
        label: `${course.name}`,
        value: course.id,
        ...course
      })) || [];
      setOptions(formattedOptions);
      setFetchedCourses(formattedOptions);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  };


  // Debounce the API call to avoid too many requests
  const debouncedFetchCourses = debounce(fetchCourses, 500);

  const hasSpecialChars = (str) => /[!@#$%^&*(),.?":{}|<>]/.test(str);

  useEffect(() => {
    if (shouldCallApi && searchValue?.label && searchValue.label.length > 3 && !hasSpecialChars(searchValue.label)) {
      debouncedFetchCourses(searchValue);
    } else if (shouldCallApi) {
      setOptions([]);
    }
    // Cleanup
    return () => {
      debouncedFetchCourses.cancel();
    };
  }, [searchValue]);

  const addCourseClick = () => {
    setShowSearchSection(true);
  };

  const hideSearchSection = () => {
    setShowSearchSection(false);
    setSearchValue('');
    setOptions([]);
  };

  const handleSearchChange = (value, type = 'input') => {
    if (!formData?.contentDeliveryMethod || !formData?.contentProvider) {
      setContentDeliveryMethodError(true);
      setSearchValue(null);
      return;
    }
    
    setContentDeliveryMethodError(false);

    if (type === 'select' && value) {
      setShouldCallApi(false);
      setSearchValue(value);
      setOptions([]);
      debouncedFetchCourses.cancel();
      return;
    }

    if (type === 'input') {
      setShouldCallApi(true);
      setSearchValue({ label: value });
    }
  };

  const addSelectedCourse = () => {
    const selectedOption = fetchedCourses.find(option => 
      option.label === searchValue.label
    );
    if (selectedOption) {
      setSelectedCourses(prevCourses => {
        const isDuplicate = isDuplicateCourse(prevCourses, selectedOption);
        const newDisplayOrder = getNewDisplayOrder(prevCourses);
        const selectedOptionWithDisplayOrder = {...selectedOption, displayOrder: newDisplayOrder};
        const newCourses = isDuplicate ? prevCourses : [...prevCourses, selectedOptionWithDisplayOrder];
        handleCourseChange?.(newCourses);
        return newCourses;
      });

      setSearchValue('');
      setOptions([]);
    }
  };


  const isDuplicateCourse = (prevCourses, selectedOption) => {
    const allCourses = [...(prevCourses), ...(formData?.courses)];

    return allCourses.some(course =>
      course.value === selectedOption.value ||
      course.id === selectedOption.id ||
      (course.label === selectedOption.label && course.externalId === selectedOption.externalId)
    );
  };

  const getNewDisplayOrder = (prevCourses) => {
    return prevCourses.length > 0 ? Math.max(...prevCourses.map(c => c.displayOrder)) + 1 : 1;
  }

  useEffect(() => {
    const items = createDraggableItems(selectedCourses);
    setDraggableItems(items);
  }, [selectedCourses]);

  useEffect(() => {
    setSelectedCourses(formData.courses);
  }, [formData?.courses]);

  useEffect(() => {
    if (formData?.contentDeliveryMethod) {
      setSearchValue('');
      setOptions([]);
    }
  }, [formData?.contentDeliveryMethod]);

  const removeCourse = (courseId) => {
    setSelectedCourses(prevCourses => {
      const newCourses = prevCourses.filter(course => (course.id !== courseId && course.value !== courseId));
      handleCourseChange?.(newCourses);
      return newCourses;
    });
  };

  const createDraggableItems = (courses = []) => {
    if (!courses) return [];
    const itemsWithOrder = courses.map((course, index) => {
      const courseId = course.id || course.value;
      return {
        id: courseId,
        title: course.name || course.label,
        subtitle: formData.contentDeliveryMethod === CONTENT_DELIVERY_METHOD.D2L ? (course.externalId || '') : course.description || '',
        displayOrder: course.displayOrder !== undefined ? course.displayOrder : index,
        actionElement: (
          <IconButton onClick={() => removeCourse(courseId)}>
            <img src={removeCourseIcon} alt="Remove course" />
          </IconButton>
        ),
      };
    });
    return itemsWithOrder.sort((a, b) => a.displayOrder - b.displayOrder);
  }

  const handleDragComplete = (updatedItems) => {
    const updatedCourses = updatedItems.map((item, index) => {
      const course = selectedCourses.find(c => c.id === item.id || c.value === item.id);
      return {
        ...course,
        displayOrder: index + 1
      };
    });
    
    setSelectedCourses(updatedCourses);
    handleCourseChange?.(updatedCourses);
  }

  return {
    addCourseClick,
    showSearchSection,
    hideSearchSection,
    searchValue,
    handleSearchChange,
    isLoading,
    options,
    addSelectedCourse,
    draggableItems,
    contentDeliveryMethodError,
    handleDragComplete
  };
};

export default useAddCourseHook;
